import type {
  EvaluationDetails,
  ExperimentEvaluation,
  TypedGet,
} from '@statsig/react-bindings';
import { useDynamicConfig } from '@statsig/react-bindings';

import { RING_FINISHES, RING_STYLES } from '@/app/consts/ring';
import { RingFinishEnum } from '@/app/enums/RingFinishEnum';
import { RingStyleEnum } from '@/app/enums/RingStyleEnum';
import type { RingFinish, RingStyle } from '@/app/types/ring';

import { IN_STOCK, OUT_OF_STOCK, SIZING_KIT } from '../_consts';
import { styleDepletionFallback } from '../_consts/inventoryInsights';
import type {
  StockStatus,
  StyleDepletionFallback,
} from '../_types/inventoryTypes';

export const DEFAULT_PRODUCTS_INVENTORY = {
  getFinishStockStatus: () => IN_STOCK as StockStatus,
  getFirstStockedFinish: () => RingFinishEnum.Silver,
  getFirstStockedStyle: () => RingStyleEnum.OuraRing4,
  getSizeStockStatus: () => IN_STOCK as StockStatus,
  getStyleStockStatus: () => IN_STOCK as StockStatus,
  isSizesHalfDepletedForFinish: () => false,
};

export interface IsOuraRing4 {
  isOuraRing4: boolean;
}

export interface GetStyleStockStatus extends IsOuraRing4 {
  style: RingStyle;
}

interface GetFinishStockStatus extends GetStyleStockStatus {
  finish: RingFinish;
}

interface GetSizeStockStatus extends GetFinishStockStatus {
  selectedSize: string;
}

export interface UseProductsInventory {
  getFirstStockedFinish: (props: GetStyleStockStatus) => RingFinish;
  getFirstStockedStyle: (props: IsOuraRing4) => RingStyle;
  getFinishStockStatus: (props: GetFinishStockStatus) => StockStatus;
  getSizeStockStatus: (props: GetSizeStockStatus) => StockStatus;
  getStyleStockStatus: (props: GetStyleStockStatus) => StockStatus;
  isSizesHalfDepletedForFinish: (props: GetFinishStockStatus) => boolean;
}

interface Config {
  readonly get: TypedGet;
  readonly name: string;
  readonly ruleID: string;
  readonly details: EvaluationDetails;
  readonly value: Record<string, unknown>;
  readonly __evaluation: ExperimentEvaluation | null;
}

interface GetFinishStock extends GetFinishStockStatus {
  gen3DepletionConfig: Config;
  or4DepletionConfig: Config;
}

const _getFinishStock = ({
  finish,
  gen3DepletionConfig,
  isOuraRing4,
  or4DepletionConfig,
  style,
}: GetFinishStock) => {
  const config = isOuraRing4 ? or4DepletionConfig : gen3DepletionConfig;
  const styleInv = config.get(
    style,
    styleDepletionFallback,
  ) as StyleDepletionFallback;
  return styleInv[finish];
};

const useProductsInventory = (): UseProductsInventory => {
  const gen3DepletionConfig = useDynamicConfig(
    'inventory_insights_gen3_depletion',
  );
  const or4DepletionConfig = useDynamicConfig('inventory_insights_or4');

  // inventory status for style
  const getStyleStockStatus = ({ style, isOuraRing4 }: GetStyleStockStatus) => {
    if (!RING_STYLES.includes(style)) return OUT_OF_STOCK;
    const config = isOuraRing4 ? or4DepletionConfig : gen3DepletionConfig;

    return (config.get(style, styleDepletionFallback) as StyleDepletionFallback)
      .stockStatus;
  };

  // inventory status for finish
  const getFinishStockStatus = ({
    style,
    finish,
    isOuraRing4,
  }: GetFinishStockStatus) => {
    if (!RING_STYLES.includes(style) || !RING_FINISHES.includes(finish))
      return OUT_OF_STOCK;
    const finishInv = _getFinishStock({
      finish,
      gen3DepletionConfig,
      isOuraRing4,
      or4DepletionConfig,
      style,
    });

    return finishInv.stockStatus;
  };

  // is inventory at least half depleted in sizes for a finish
  const isSizesHalfDepletedForFinish = ({
    finish,
    isOuraRing4,
    style,
  }: GetFinishStockStatus): boolean => {
    const finishInv = _getFinishStock({
      finish,
      gen3DepletionConfig,
      isOuraRing4,
      or4DepletionConfig,
      style,
    });
    const sizes = finishInv.sizes;
    const totalSizes = sizes.length;
    const outOfStockCount = sizes.filter(
      (ring) => ring.stockStatus === OUT_OF_STOCK,
    ).length;
    return totalSizes / 2 <= outOfStockCount;
  };

  // inventory status for size
  const getSizeStockStatus = ({
    style,
    finish,
    selectedSize,
    isOuraRing4,
  }: GetSizeStockStatus) => {
    if (!RING_STYLES.includes(style) || !RING_FINISHES.includes(finish))
      return IN_STOCK;
    const config = isOuraRing4 ? or4DepletionConfig : gen3DepletionConfig;
    const finishInv = (
      config.get(style, styleDepletionFallback) as StyleDepletionFallback
    )[finish];

    const sizes = finishInv.sizes;
    if (selectedSize === SIZING_KIT) {
      return IN_STOCK;
    }
    return (
      sizes.find((ring) => ring.size == Number(selectedSize))?.stockStatus ??
      IN_STOCK
    );
  };

  // get the first stocked style
  const getFirstStockedStyle = ({ isOuraRing4 }: { isOuraRing4: boolean }) =>
    RING_STYLES.find(
      (style) => getStyleStockStatus({ style, isOuraRing4 }) !== OUT_OF_STOCK,
    ) ?? RingStyleEnum.OuraRing4;

  // get the first stocked finish
  const getFirstStockedFinish = ({
    isOuraRing4,
    style,
  }: {
    isOuraRing4: boolean;
    style: RingStyle;
  }) => {
    const inStockFinish = RING_FINISHES.find((finish) => {
      const stockedFinish = getFinishStockStatus({
        finish,
        isOuraRing4,
        style,
      });
      return stockedFinish !== OUT_OF_STOCK;
    });

    return inStockFinish ?? RingFinishEnum.Silver;
  };

  return {
    getFinishStockStatus,
    getFirstStockedFinish,
    getFirstStockedStyle,
    getSizeStockStatus,
    getStyleStockStatus,
    isSizesHalfDepletedForFinish,
  };
};

export default useProductsInventory;
